// Monaco-side wiring that makes the synthesized peek models behave like real
// (read-only) documents.
//
// Two hooks, installed once per renderer:
//
//  1. `registerEditorOpener` — the peek widget's "open" gesture (double-click /
//     Enter / Ctrl-click on a row) calls `ICodeEditorService.openCodeEditor`
//     with the reference's URI. Standalone Monaco can only open a URI that is
//     already attached to a visible editor, so without this hook a
//     `jamsql-ref:` row would be a dead end. We claim the URI and route it
//     through the SAME `openReferenceLocation` path the ReferencesTab rows use
//     (CREATE-text query tab, scrolled to the reference).
//
//  2. `onDidCreateEditor` — the peek widget's embedded preview editor inherits
//     the HOST editor's raw options (`EmbeddedCodeEditorWidget` constructor),
//     so it is editable by default. Typing in it would mutate a synthesized
//     model and read as "editing the database object". The guard flips the
//     preview to `readOnly` whenever it is showing one of our models, and
//     restores the inherited value when it switches back to a live buffer.
//     It covers the Go to Definition target models (`jamsql-nav:`, shown by
//     Peek Definition on a table/view, column or routine) the same way.

import type * as monacoNs from 'monaco-editor';

import type { SqlRange } from '../types';

import { isDefinitionNavScheme } from '../go-to/definitionNavTarget';
import { countInBufferDefinitionOpen } from '../go-to/definitionNavigationOpener';

import { lookupPeekReferenceModel } from './peekReferenceModels';
import { isPeekReferenceScheme } from './peekReferenceUri';
import { openReferenceLocation } from './openReferenceLocation';

/** Structural subset of the Monaco namespace the integration needs. Both hooks
 *  are optional: partial Monaco stubs (unit tests, embedded hosts) must not
 *  break the editor just because the peek niceties cannot be installed. */
export type PeekIntegrationMonacoApi = {
  readonly editor: {
    registerEditorOpener?: (opener: monacoNs.editor.ICodeEditorOpener) => monacoNs.IDisposable;
    onDidCreateEditor?: (
      listener: (codeEditor: monacoNs.editor.ICodeEditor) => void,
    ) => monacoNs.IDisposable;
  };
};

const READ_ONLY_MESSAGE =
  'Read-only preview of a stored database object. Open it from the peek list to edit a copy in a query tab.';
const DEFINITION_NAV_READ_ONLY_MESSAGE =
  'Read-only Go to Definition target. Open it from the peek list to navigate there.';

function readOnlyMessageFor(scheme: string | undefined): string | null {
  if (isPeekReferenceScheme(scheme)) return READ_ONLY_MESSAGE;
  if (isDefinitionNavScheme(scheme)) return DEFINITION_NAV_READ_ONLY_MESSAGE;
  return null;
}

let installed: monacoNs.IDisposable | null = null;

/** Idempotent install. Returns a disposable that removes both hooks; calling
 *  it twice is a no-op (the second call returns the live handle). */
export function installPeekReferenceIntegration(
  monaco: PeekIntegrationMonacoApi,
): monacoNs.IDisposable {
  if (installed) return installed;
  const hooks: monacoNs.IDisposable[] = [];
  if (typeof monaco.editor.registerEditorOpener === 'function') {
    hooks.push(
      monaco.editor.registerEditorOpener({
        openCodeEditor: (_source, resource, selectionOrPosition) =>
          handlePeekOpen(resource, selectionOrPosition),
      }),
    );
  }
  if (typeof monaco.editor.onDidCreateEditor === 'function') {
    hooks.push(monaco.editor.onDidCreateEditor((codeEditor) => attachReadOnlyGuard(codeEditor)));
  }
  installed = {
    dispose: () => {
      for (const hook of hooks) hook.dispose();
      installed = null;
    },
  };
  return installed;
}

/** Test-only — forget the install latch so a fresh fake Monaco can be wired. */
export function __resetPeekReferenceIntegrationForTest(): void {
  installed?.dispose();
  installed = null;
}

/** Claim a `jamsql-ref:` open request and hand it to the workspace. Returns
 *  `false` for every other URI so Monaco falls through to its own handlers.
 *
 *  The workspace call is DEFERRED to a macrotask on purpose: in peek mode
 *  Monaco's `_gotoReference` re-shows the peek widget in the `.then()` of this
 *  very call, so adding a tab (and unmounting the host editor with it) inside
 *  the handler would leave `show()` running against a disposed widget.
 *  `schedule` is injectable so unit tests can run the effect synchronously. */
export function handlePeekOpen(
  resource: { readonly scheme?: string; toString(): string },
  selectionOrPosition?: monacoNs.IRange | monacoNs.IPosition,
  schedule: (run: () => void) => void = defaultSchedule,
): boolean {
  if (!isPeekReferenceScheme(resource.scheme)) return false;
  const entry = lookupPeekReferenceModel(resource.toString());
  if (!entry) return false;
  // An alias / CTE Go to Definition inside a stored-object preview lands here
  // (this opener claims the open and may run before the Go to Definition
  // opener, which then never sees it), so count it here too. The count
  // consumes its match, so an open seen by both openers counts once.
  countInBufferDefinitionOpen(resource, selectionOrPosition);
  const range = sqlRangeFromMonacoTarget(selectionOrPosition);
  schedule(() => {
    void openReferenceLocation({
      connectionId: entry.connectionId,
      database: entry.database,
      engineId: entry.engineId,
      objectKind: entry.objectKind,
      objectName: entry.objectName,
      range,
      bodyText: entry.bodyText,
    });
  });
  return true;
}

function defaultSchedule(run: () => void): void {
  setTimeout(run, 0);
}

function attachReadOnlyGuard(codeEditor: monacoNs.editor.ICodeEditor): void {
  // The restore value must be the editor's OWN inherited readOnly, not a
  // hardcoded `false`: the reference provider is registered per language id,
  // so read-only hosts (job-scheduler's ReadOnlySqlViewer, the PL/SQL
  // debugger's paused editor) get the peek too, and forcing `false` on the
  // way out would let the user type into a surface that promised read-only.
  let inheritedReadOnly: boolean | null = null;
  const sync = (): void => {
    const message = readOnlyMessageFor(codeEditor.getModel?.()?.uri?.scheme);
    if (message !== null) {
      if (inheritedReadOnly === null) {
        inheritedReadOnly = readRawReadOnly(codeEditor);
      }
      codeEditor.updateOptions?.({
        readOnly: true,
        readOnlyMessage: { value: message },
      });
      return;
    }
    if (inheritedReadOnly === null) return;
    codeEditor.updateOptions?.({ readOnly: inheritedReadOnly });
    inheritedReadOnly = null;
  };
  if (typeof codeEditor.onDidChangeModel === 'function') codeEditor.onDidChangeModel(sync);
  sync();
}

function readRawReadOnly(codeEditor: monacoNs.editor.ICodeEditor): boolean {
  const raw = (
    codeEditor as { getRawOptions?: () => { readOnly?: boolean } }
  ).getRawOptions?.();
  return raw?.readOnly === true;
}

/** Monaco hands 1-indexed line/column; `SqlRange` is 0-indexed. Only the start
 *  is meaningful downstream (`openReferenceLocation` reveals the start), but
 *  the whole range is filled so the value is a valid `SqlRange`. */
function sqlRangeFromMonacoTarget(
  target: monacoNs.IRange | monacoNs.IPosition | undefined,
): SqlRange {
  const startLine = readNumber(target, 'startLineNumber', 'lineNumber');
  const startColumn = readNumber(target, 'startColumn', 'column');
  const endLine = readNumber(target, 'endLineNumber', 'lineNumber', startLine);
  const endColumn = readNumber(target, 'endColumn', 'column', startColumn);
  return {
    startOffset: 0,
    endOffset: 0,
    startLine: Math.max(0, startLine - 1),
    startColumn: Math.max(0, startColumn - 1),
    endLine: Math.max(0, endLine - 1),
    endColumn: Math.max(0, endColumn - 1),
  };
}

function readNumber(
  target: unknown,
  primary: string,
  fallbackKey: string,
  fallbackValue = 1,
): number {
  if (!target || typeof target !== 'object') return fallbackValue;
  const bag = target as Record<string, unknown>;
  const direct = bag[primary];
  if (typeof direct === 'number') return direct;
  const alt = bag[fallbackKey];
  if (typeof alt === 'number') return alt;
  return fallbackValue;
}
